import {ChangeEvent, FC, KeyboardEvent, useEffect, useMemo, useRef, useState} from 'react';
import { useDispatch } from 'react-redux';

import {
  CloseCircleOutlined,
} from '@ant-design/icons';

import { Input } from 'antd';

// Shared UI Library Components
import { CustomIcon, EIconName, Loader, TElementMode } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './chat-input.scss';

// Store
import { openNotification } from '../../../../store/app-notifications/app-notifications.thunks';

// Utils
import { isWidgetAppType } from '../../../../utils/widget.utils';
import {PreviewFileModal} from "../../../../modals/preview-file-modal/preview-file-modal";
import {getFileIcon} from "../../../../utils/getFileIcon.utils";

const TextArea = Input.TextArea;

interface IMessageInputProps {
  currValue: string;
  disabled?: boolean;
  showFiles?: boolean;
  loading?: boolean;
  mode?: TElementMode;
  placeholder?: string;
  sendMessage?: (message: string, files: File[]) => void;
  clearFiles: boolean;
}

// Allowed file types
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'application/zip',
  'application/x-zip-compressed',
  'application/x-rar-compressed',
  'application/vnd.rar'
];

const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10485760; // 10MB
const MAX_MESSAGE_LENGTH = 2500;

export const ChatInput: FC<IMessageInputProps> = (
  {
    currValue,
    mode = 'white',
    showFiles = true,
    placeholder = 'Message',
    disabled = false,
    loading = false,
    sendMessage = () => {},
    clearFiles
  }: IMessageInputProps
) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<any>(null);

  const [inputValue, setInputValue] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  const dispatch = useDispatch<any>();


  const imageFiles = useMemo(() => {
    return files.filter(file => IMAGE_TYPES.includes(file.type));
  }, [files]);

  const isWidget = isWidgetAppType();

  // Validating files
  const validateFiles = (filesToValidate: File[]): { valid: File[], errors: string[] } => {
    const valid: File[] = [];
    const errors: string[] = [];

    for (const file of filesToValidate) {
      // Check file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        errors.push(`File "${file.name}" has unsupported format`);
        continue;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`File "${file.name}" exceeds 10MB limit`);
        continue;
      }

      valid.push(file);
    }

    return { valid, errors };
  };

  // Adding files
  const addFiles = (newFiles: File[]): void => {
    const remainingSlots = MAX_FILES - files.length;

    if (remainingSlots === 0) {
      dispatch(openNotification({
        type: 'warning',
        description: `Maximum ${MAX_FILES} files allowed`
      }));
      return;
    }

    if (newFiles.length > remainingSlots) {
      dispatch(openNotification({
        type: 'warning',
        description: `You can add only ${remainingSlots} more file(s)`
      }));
    }

    const filesToAdd = newFiles.slice(0, remainingSlots);
    const { valid, errors } = validateFiles(filesToAdd);

    if (errors.length > 0) {
      errors.forEach(error => {
        dispatch(openNotification({
          type: 'error',
          description: error
        }));
      });
    }

    if (valid.length > 0) {
      setFiles(prev => [...prev, ...valid]);
    }
  };

  const onUploadFileHandler = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files) return;

    const newFiles = Array.from(e.target.files);
    addFiles(newFiles);

    // Clear input
    inputFileRef.current!.value = '';

    // Clear files, if same without clear, input not emit uploading
    inputFileRef.current!.value = '';
  }

  // Paste from Ctrl+V
  const onPasteHandler = (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const pastedFiles: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          pastedFiles.push(file);
        }
      }
    }

    if (pastedFiles.length > 0) {
      e.preventDefault();
      addFiles(pastedFiles);
    }
  };

  // Delete added file
  const removeFile = (index: number): void => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Open modal for preview
  const openPreview = (file: File): void => {
    if (IMAGE_TYPES.includes(file.type)) {
      const imageIndex = imageFiles.findIndex(img => img === file);
      setCurrentImageIndex(imageIndex);
      setPreviewModalOpen(true);
    }
  };

  const openUploadFileWindow = (): void => {
    if (files.length === 5) {
      dispatch(openNotification({
        type: 'error',
        description: `You can upload only 5 files`
      }));
    } else {
      inputFileRef.current?.click();
    }
  };

  const onChangeHandler = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    const currValue = e.target.value;
    setInputValue(currValue);
  }

  const onKeyDownHandler = (event: KeyboardEvent): void => {
    if (event.code === 'Enter' && !event.shiftKey) {
      event.preventDefault();
    }
    if (event.code !== 'Enter' || event.shiftKey) return;

    // Validation before submission
    if (!inputValue.trim().length && files.length === 0) {
      dispatch(openNotification({
        type: 'warning',
        description: 'Please enter a message or attach at least one file'
      }));
      return;
    }
    onSendMessageHandler();
    // Enter -> send message
    // Shift + Enter -> next line
  }

  const onResizeHandler = (size: { width: number, height: number }): void => {
    document.documentElement.style.setProperty("--overHeight", `${ size.height - 55 }px`);
  }

  useEffect(() => {
    if (files.length > 0) {
      document.documentElement.style.setProperty("--previewBlockHeight", '50px');
    } else {
      document.documentElement.style.setProperty("--previewBlockHeight", '0px');
    }
  }, [files]);

  const onSendMessageHandler = (): void => {
    // Validation before submission
    const trimmedMessage = inputValue.trim();

    if (!trimmedMessage && files.length === 0) {
      dispatch(openNotification({
        type: 'warning',
        description: 'Please enter a message or attach at least one file'
      }));
      return;
    }

    if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
      dispatch(openNotification({
        type: 'error',
        description: `Message exceeds ${MAX_MESSAGE_LENGTH} characters limit`
      }));
      return;
    }

    sendMessage(inputValue, files);
  };

  useEffect(() => {
    if (!currValue) {
      setInputValue('');
      setFiles([]);
    }
  }, [currValue, clearFiles]);

  useEffect(() => {
    return () => {
      document.documentElement.style.setProperty("--overHeight", '0px');
    };
  }, []);

  // Display of added files
  const renderFiles = () => {
    if (files.length === 0) return null;

    return files.map((file, index) => {
      const isImage = IMAGE_TYPES.includes(file.type);
      const fileUrl = isImage ? URL.createObjectURL(file) : null;

      return (
        <div key={index} className="chat-input--file-item file-item">
          <div
            className="file-item__file"
            onClick={() => isImage && openPreview(file)}
            style={{ cursor: isImage ? 'pointer' : 'default' }}
          >
            {isImage && fileUrl ? (
              <img
                src={fileUrl}
                alt={file.name}
              />
            ) : (
              getFileIcon(file)
            )}
          </div>
          <CloseCircleOutlined
            className="file-item__close-btn"
            onClick={() => removeFile(index)}
          />
        </div>
      );
    });
  };

  return (
    <div className={ `chat-input chat-input-${ mode }` }>
      {files.length > 0 && (
        <div className="chat-input--files-preview">
          {renderFiles()}
        </div>
      )}

      <div className="chat-input--item">
        { showFiles && <CustomIcon name={ EIconName.AttachFile } onClick={ openUploadFileWindow } className={files.length === 5 ? 'uploadBtn disabled' : 'uploadBtn'} /> }
        <TextArea
          ref={textAreaRef}
          onChange={ onChangeHandler }
          onKeyDown={ onKeyDownHandler }
          onPaste={onPasteHandler}
          onResize={ onResizeHandler }
          value={ inputValue }
          showCount={ true }
          maxLength={MAX_MESSAGE_LENGTH}
          autoSize={ isWidget ? {
            maxRows: 3
          } : {
            maxRows: 10
          } }
          placeholder={ placeholder }
          disabled={ disabled }
        />

        { !loading &&
          <CustomIcon className={ `send-input ${ !inputValue.trim().length ? 'disabled' : '' }` }
                      name={ EIconName.Send }
                      onClick={ onSendMessageHandler }/> }

        { loading && <Loader className='loader'/> }

        { showFiles && <div className="chat-input--item--files">Files {files.length}/{MAX_FILES} (max size 10MB each)</div> }
      </div>
      <input
        accept={[
          'image/jpeg',
          'image/jpg',
          'image/png',
          'image/webp',
          'application/pdf',
          '.zip,.rar',
          '.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          '.ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'text/plain'
        ].join(',')}
        ref={inputFileRef}
        type="file"
        multiple={true}
        hidden={true}
        onChange={onUploadFileHandler}
      />

      <PreviewFileModal
        open={previewModalOpen}
        images={imageFiles}
        currentIndex={currentImageIndex}
        onIndexChange={setCurrentImageIndex}
        onCancel={() => {
          setPreviewModalOpen(false);
          setCurrentImageIndex(0);
        }}
      />
    </div>
  )
};
